import Axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { env } from '@/config/env';
import { useStore } from '@/stores';
import { router } from '@/routes/router';

const DEFAULT_TIMEOUT = 60000;

export interface IError {
  errors: {
    detail: string;
    id: string;
    title: string;
  }[];
}

export class AxiosManager {
  public readonly http: AxiosInstance;
  public readonly httpWithAuth: AxiosInstance;
  private readonly httpRefreshToken: AxiosInstance;

  constructor() {
    this.http = Axios.create({
      baseURL: env.baseUrl,
      timeout: DEFAULT_TIMEOUT,
    });

    this.httpWithAuth = Axios.create({
      baseURL: env.baseUrl,
      timeout: DEFAULT_TIMEOUT,
    });

    this.httpRefreshToken = Axios.create({
      baseURL: env.baseUrl,
      timeout: DEFAULT_TIMEOUT,
    });

    this.http.interceptors.response.use(
      this.fulfilledResponseInterceptor,
      this.unauthenticatedRejectResponseInterceptor,
    );

    this.httpWithAuth.interceptors.request.use(this.authRequestInterceptor);
    this.httpWithAuth.interceptors.response.use(
      this.fulfilledResponseInterceptor,
      this.rejectResponseInterceptor.bind(this),
    );

    this.httpRefreshToken.interceptors.response.use(
      this.fulfilledResponseInterceptor,
      this.refreshTokenRejectResponseInterceptor,
    );
  }

  private async authRequestInterceptor(axiosConfig: InternalAxiosRequestConfig) {
    const token = useStore.getState().accessToken;

    if (axiosConfig.headers) {
      if (token) axiosConfig.headers.authorization = token;
      axiosConfig.headers.Accept = 'application/json';
      axiosConfig.headers['Is-Live'] = env.doitpayMerchantIsLive;
    }

    return axiosConfig;
  }

  private async fulfilledResponseInterceptor(response: AxiosResponse) {
    return response;
  }

  private async unauthenticatedRejectResponseInterceptor(error: AxiosError<IError>) {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      console.warn(
        `Are you sure you use axiosWithToken for ${originalRequest.method?.toUpperCase()} /${
          originalRequest.url
        } ? This is a restricted endpoint.`,
      );
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }

  private async rejectResponseInterceptor(error: AxiosError<IError>) {
    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      await this.refreshAccessToken();
      return await this.httpWithAuth.request(originalRequest);
    }

    return Promise.reject(error);
  }

  private async refreshTokenRejectResponseInterceptor(error: AxiosError<IError>) {
    if (error.response?.status === 401) {
      useStore.setState({ accessToken: null, refreshToken: null });
      useStore.getState().setSelectedBusiness(null);
      const currentPath = window.location.pathname + window.location.search;
      router.navigate({
        to: '/auth/login',
        search: { redirect: currentPath },
      });
      return;
    }
  }

  private async refreshAccessToken() {
    const oldAccessToken = useStore.getState().accessToken;

    const { data } = await this.httpRefreshToken.post<{
      access_token: string;
    }>('dashboard/v1/authenticate/access-token', {
      access_token: oldAccessToken,
    });

    useStore.setState({ accessToken: data.access_token });
    this.httpWithAuth.defaults.headers.common.Authorization = data.access_token;
  }
}
